import React, { useState, useEffect, useRef, useMemo } from 'react';
import { FaSearch, FaChevronDown, FaChevronUp, FaPlus, FaEdit, FaTrash, FaCamera, FaHistory } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import api, { BASE_URL } from '../../../utils/api';
import { useAuth } from '../../../context/AuthContext';
import useColleges from '../../../utils/useColleges';
import { toTitleCase, phoneChange, isValidPhone, INDIAN_STATES } from '../../../utils/formHelpers';
import StateSelect from '../../../components/common/StateSelect/StateSelect';
import './AddEmployee.css';

const API_BASE = BASE_URL;

const EXPERIENCES = [
  '0-1 Year', '1-2 Years', '2-3 Years', '3-4 Years', '4-5 Years', 
  '5-6 Years', '6-7 Years', '7-8 Years', '8-9 Years', '9-10 Years', '10+ Years'
];
const RELIGIONS = ['Hindu', 'Muslim', 'Sikh', 'Christian', 'Jain', 'Buddha'];
const CATEGORIES = ['General', 'OBC', 'SC', 'ST', 'EWS'];

const EMPTY_FORM = {
  name: '', fatherName: '', dob: '', dateOfAnniversary: '', gender: '', religion: '', category: '',
  contact: '', email: '', qualification: '', department: '', college: '', teachingType: '',
  experience: '', lastEmployer: '',
  bankName: '', accountNumber: '', ifscCode: '', accountType: '',
  monthlySalary: '', securityMoney: '', monthlyDeduction: '', pfDeduction: '', esiDeduction: '', netDailySalary: '',
  address: '', country: 'India', state: '', city: ''
};

const AddEmployee = () => {
  const { user }  = useAuth();
  const isAdmin   = user?.role === 'admin';
  const { colleges, loading: collegesLoading } = useColleges();
  const navigate  = useNavigate();
  const basePath  = user?.role === 'collegeadmin' ? '/collegeadmin' : '/admin';

  const [employees, setEmployees]           = useState([]);
  const [departments, setDepartments]       = useState([]);
  const [loading, setLoading]               = useState(false);
  const [saving, setSaving]                 = useState(false);
  
  // Search & Filters
  const [searchQuery, setSearchQuery]       = useState('');
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [filterDepartment, setFilterDepartment] = useState('');
  
  // Form State
  const [showAddForm, setShowAddForm]       = useState(false);
  const [form, setForm]                     = useState(EMPTY_FORM);
  const [photoFile, setPhotoFile]           = useState(null);
  const [previewPhoto, setPreviewPhoto]     = useState(null);
  const [editId, setEditId]                 = useState(null);
  const [expandedRow, setExpandedRow]       = useState(null);

  const searchRef = useRef(null);

  // ── Load Departments & Colleges ─────────────────────────────────
  useEffect(() => {
    api.get('/departments').then(r => setDepartments(r.data.data || []));
  }, []);

  // ── Reload departments when college changes ────────────────────────
  useEffect(() => {
    const collegeId = isAdmin ? form.college : (user?.college?._id || user?.college);
    if (collegeId) {
      api.get('/departments', { params: { college: collegeId } })
        .then(r => {
          // Filter only active departments
          const activeDepts = (r.data.data || []).filter(d => d.isActive !== false);
          setDepartments(activeDepts);
        });
    } else {
      setDepartments([]);
    }
    // Reset department selection when college changes
    if (isAdmin) {
      setForm(f => ({ ...f, department: '' }));
    }
  }, [form.college, isAdmin]);

  // ── Load Employees ──────────────────────────────────────────────
  const loadEmployees = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/employees', { params: { limit: 500 } });
      setEmployees(data.data || []);
    } catch {
      Swal.fire('Error', 'Failed to load employees.', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadEmployees(); }, []);

  // ── Load departments for collegeadmin on mount ──────────────────────
  useEffect(() => {
    if (!isAdmin) {
      const collegeId = user?.college?._id || user?.college;
      if (collegeId) api.get('/departments', { params: { college: collegeId } })
        .then(r => {
          // Filter only active departments
          const activeDepts = (r.data.data || []).filter(d => d.isActive !== false);
          setDepartments(activeDepts);
        });
    }
  }, [isAdmin, user]);

  // Close search drop on outside click
  useEffect(() => {
    const handler = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) setShowSearchDropdown(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const titleFields = ['name','fatherName','address','city','lastEmployer','qualification'];
    const phoneFields = ['contact'];
    if (phoneFields.includes(name)) {
      let digits = value.replace(/\D/g, '');
      if (digits.length > 0 && !/^[5-9]/.test(digits)) digits = digits.slice(1);
      if (digits.length > 10) digits = digits.slice(0, 10);
      setForm(f => ({ ...f, [name]: digits }));
      return;
    }
    setForm(f => {
      const finalValue = titleFields.includes(name) ? toTitleCase(value) : value;
      const updated = { ...f, [name]: finalValue };
      if (name === 'monthlySalary') {
        updated.netDailySalary = value ? (parseFloat(value) / 30).toFixed(2) : '';
      }
      return updated;
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setPhotoFile(file);
    setPreviewPhoto(URL.createObjectURL(file));
  };

  const validateForm = () => {
    const required = [
      { field: 'name', label: 'Full Name' },
      { field: 'fatherName', label: "Father's Name" },
      { field: 'dob', label: 'Date of Birth' },
      { field: 'contact', label: 'Contact Number' },
      { field: 'department', label: 'Department' },
      { field: 'monthlySalary', label: 'Monthly Salary' }
    ];
    
    for (const req of required) {
      if (!form[req.field] || form[req.field].toString().trim() === '') {
        Swal.fire('Missing Field', `Please fill the "${req.label}" field!`, 'warning');
        return false;
      }
    }
    
    // Validate DOB - employee must be >= 18 years old
    if (form.dob) {
      const dob = new Date(form.dob);
      const today = new Date();
      const age = today.getFullYear() - dob.getFullYear();
      const monthDiff = today.getMonth() - dob.getMonth();
      const dayDiff = today.getDate() - dob.getDate();
      
      const actualAge = monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
      
      if (actualAge < 18) {
        Swal.fire('Invalid Age', 'Employee must be at least 18 years old!', 'warning');
        return false;
      }
    }
    
    return true;
  };

  const resetForm = () => {
    setForm(EMPTY_FORM);
    setPhotoFile(null);
    setPreviewPhoto(null);
    setEditId(null);
    setShowAddForm(false);
  };

  // ── Save ────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!validateForm()) return;
    setSaving(true);
    try {
      const fd = new FormData();
      Object.keys(form).forEach(key => fd.append(key, form[key]));
      if (photoFile) fd.append('photo', photoFile);

      const opts = { headers: { 'Content-Type': 'multipart/form-data' } };

      if (editId) {
        const { data } = await api.put(`/employees/${editId}`, fd, opts);
        setEmployees(prev => prev.map(e => e._id === editId ? data.data : e));
        Swal.fire('Updated!', 'Employee has been updated.', 'success');
      } else {
        const { data } = await api.post('/employees', fd, opts);
        setEmployees(prev => [data.data, ...prev]);
        Swal.fire('Added!', 'New employee has been added.', 'success');
      }
      resetForm();
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to save employee.', 'error');
    } finally {
      setSaving(false);
    }
  };

  // ── Edit & Delete ───────────────────────────────────────────────
  const handleEdit = (emp) => {
    setEditId(emp._id);
    const collegeId = emp.college?._id || emp.college || '';
    const departmentId = emp.department?._id || emp.department || '';
    
    setForm({
      name: emp.name || '', fatherName: emp.fatherName || '', dob: emp.dob ? emp.dob.split('T')[0] : '', 
      dateOfAnniversary: emp.dateOfAnniversary ? emp.dateOfAnniversary.split('T')[0] : '',
      gender: emp.gender || '', religion: emp.religion || '', category: emp.category || '',
      contact: emp.contact || '', email: emp.email || '', qualification: emp.qualification || '', 
      department: departmentId,
      college: collegeId,
      teachingType: emp.teachingType || '', experience: emp.experience || '', 
      lastEmployer: emp.lastEmployer || '',
      bankName: emp.bankName || '', accountNumber: emp.accountNumber || '', ifscCode: emp.ifscCode || '', accountType: emp.accountType || '',
      monthlySalary: emp.monthlySalary || '', securityMoney: emp.securityMoney || '', monthlyDeduction: emp.monthlyDeduction || '',
      pfDeduction: emp.pfDeduction || '', esiDeduction: emp.esiDeduction || '',
      netDailySalary: emp.monthlySalary ? (parseFloat(emp.monthlySalary) / 30).toFixed(2) : (emp.netDailySalary || ''),
      address: emp.address || '', country: emp.country || '', state: emp.state || '', city: emp.city || ''
    });
    setPhotoFile(null);
    setPreviewPhoto(emp.photo ? `${API_BASE}/${emp.photo}` : null);
    setShowAddForm(true);
    setExpandedRow(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = (emp) => {
    Swal.fire({
      title: `Delete "${emp.name}"?`, text: 'This cannot be undone.', icon: 'warning',
      showCancelButton: true, confirmButtonColor: '#e74c3c', confirmButtonText: 'Yes, delete it',
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      try {
        await api.delete(`/employees/${emp._id}`);
        setEmployees(prev => prev.filter(e => e._id !== emp._id));
        Swal.fire('Deleted!', 'Employee deleted.', 'success');
      } catch (err) {
        Swal.fire('Error', err.response?.data?.message || 'Delete failed.', 'error');
      }
    });
  };

  const toggleStatus = async (emp) => {
    try {
      const { data } = await api.patch(`/employees/${emp._id}/toggle`);
      setEmployees(prev => prev.map(e => e._id === emp._id ? data.data : e));
    } catch (err) {
      Swal.fire('Error', err.response?.data?.message || 'Failed to update status.', 'error');
    }
  };

  const getPhotoUrl = (emp) => emp.photo ? `${API_BASE}/${emp.photo.replace(/\\/g, '/')}` : null;

  // ── Filters ─────────────────────────────────────────────────────
  const uniqueDepartments = useMemo(() => {
    return [...new Set(employees.map(e => e.department?.name || e.department).filter(Boolean))].sort();
  }, [employees]);

  const displayedEmployees = useMemo(() => {
    let res = employees;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      res = res.filter(e => e.name.toLowerCase().includes(q) || (e.contact && e.contact.includes(q)));
    }
    if (filterDepartment) {
      res = res.filter(e => (e.department?.name || e.department) === filterDepartment);
    }
    return res;
  }, [employees, searchQuery, filterDepartment]);

  const grouped = useMemo(() => {
    const g = {};
    displayedEmployees.forEach(emp => {
      const collegeId = emp.college?._id || emp.college;
      const found = colleges.find(c => String(c._id) === String(collegeId));
      const key = found?.name || emp.college?.name || 'Unassigned';
      if (!g[key]) g[key] = [];
      g[key].push(emp);
    });
    return g;
  }, [displayedEmployees, colleges]);

  return (
    <div className="add-employee-container">
      <h2 className="add-employee-title">Employee Directory</h2>

      {/* ── Top Controls ── */}
      <div className="add-employee-controls">
        <div className="add-employee-search-wrapper" ref={searchRef}>
          <FaSearch className="add-employee-search-icon" />
          <input
            type="text"
            className="add-employee-search-input"
            placeholder="Search by name or contact..."
            value={searchQuery}
            onChange={(e) => { setSearchQuery(e.target.value); setShowSearchDropdown(true); }}
            onFocus={() => setShowSearchDropdown(true)}
          />
          {showSearchDropdown && searchQuery && (
            <div className="add-employee-search-dropdown">
              <ul>
                {displayedEmployees.length > 0 ? displayedEmployees.map(e => (
                  <li key={e._id} onClick={() => { setSearchQuery(e.name); setShowSearchDropdown(false); }}>
                    {e.name} {e.contact ? `(${e.contact})` : ''}
                  </li>
                )) : (
                  <div className="add-employee-search-empty">No employees found</div>
                )}
              </ul>
            </div>
          )}
        </div>

        <select className="add-employee-filter-select" value={filterDepartment} onChange={e => setFilterDepartment(e.target.value)}>
          <option value="">All Departments</option>
          {uniqueDepartments.map(d => <option key={d} value={d}>{d}</option>)}
        </select>

        <button className="add-employee-btn add-employee-btn-add" onClick={() => { if (showAddForm) resetForm(); else setShowAddForm(true); }}>
          <FaPlus /> {showAddForm ? 'Close Form' : 'Add Employee'}
        </button>
      </div>

      {/* ── Add / Edit Form ── */}
      {showAddForm && (
        <div className="add-employee-inline-form">
          <h3 className="add-employee-form-title">{editId ? '✏️ Edit Employee' : 'Add New Employee'}</h3>

          {/* Photo Section */}
          <div className="add-employee-photo-upload-wrapper">
            <div className="add-employee-photo-preview">
              {previewPhoto ? <img src={previewPhoto} alt="Preview" /> : <FaCamera className="add-employee-placeholder-icon" />}
            </div>
            <div className="add-employee-upload-controls">
              <label className="add-employee-upload-btn">
                {previewPhoto ? 'Change Photo' : 'Upload Passport Photo'}
                <input type="file" accept="image/*" onChange={handlePhotoChange} style={{ display: 'none' }} />
              </label>
              <span className="add-employee-upload-hint">Passport Size (JPG, PNG) max 5MB</span>
            </div>
          </div>

          {/* Section: Personal Info */}
          <h4 className="add-employee-section-title">Personal Details</h4>
          <div className="add-employee-form-grid">
            <div className="add-employee-form-group">
              <label>Full Name <span className="add-employee-req">*</span></label>
              <input type="text" name="name" value={form.name} onChange={handleInputChange} placeholder="Enter name" />
            </div>
            <div className="add-employee-form-group">
              <label>Father's Name <span className="add-employee-req">*</span></label>
              <input type="text" name="fatherName" value={form.fatherName} onChange={handleInputChange} placeholder="Enter father's name" />
            </div>
            <div className="add-employee-form-group">
              <label>Date of Birth <span className="add-employee-req">*</span></label>
              <input type="date" name="dob" value={form.dob} onChange={handleInputChange} />
              <span className="add-employee-upload-hint" style={{fontSize:'0.7rem',color:'#64748b'}}>Employee must be 18+ years old</span>
            </div>
            <div className="add-employee-form-group">
              <label>Date of Anniversary</label>
              <input type="date" name="dateOfAnniversary" value={form.dateOfAnniversary} onChange={handleInputChange} />
            </div>
            <div className="add-employee-form-group">
              <label>Gender</label>
              <select name="gender" value={form.gender} onChange={handleInputChange}>
                <option value="">-- Select --</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Transgender">Transgender</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="add-employee-form-group">
              <label>Religion</label>
              <select name="religion" value={form.religion} onChange={handleInputChange}>
                <option value="">-- Select --</option>
                {RELIGIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="add-employee-form-group">
              <label>Category</label>
              <select name="category" value={form.category} onChange={handleInputChange}>
                <option value="">-- Select --</option>
                {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>

          {/* Section: Contact & Address */}
          <h4 className="add-employee-section-title">Contact & Address</h4>
          <div className="add-employee-form-grid">
            <div className="add-employee-form-group">
              <label>Contact Number <span className="add-employee-req">*</span></label>
              <input type="text" name="contact" value={form.contact} onChange={handleInputChange} placeholder="Phone number" />
            </div>
            <div className="add-employee-form-group">
              <label>Email ID</label>
              <input type="email" name="email" value={form.email} onChange={handleInputChange} placeholder="Email address" />
            </div>
            <div className="add-employee-form-group add-employee-col-span-2">
              <label>Address</label>
              <input type="text" name="address" value={form.address} onChange={handleInputChange} placeholder="Street, Area" />
            </div>
            <div className="add-employee-form-group">
              <label>City</label>
              <input type="text" name="city" value={form.city} onChange={handleInputChange} placeholder="City" />
            </div>
            <div className="add-employee-form-group">
              <label>State</label>
              <StateSelect
                value={form.state}
                onChange={v => setForm(f => ({ ...f, state: v }))}
              />
            </div>
            <div className="add-employee-form-group">
              <label>Country</label>
              <input type="text" value="India" readOnly disabled style={{background:'#f1f5f9',cursor:'not-allowed'}} />
            </div>
          </div>

          {/* College Assignment */}
          <h4 className="add-employee-section-title">🏫 College Assignment — Employee belongs to which college?</h4>
          <div className="add-employee-form-grid">
            <div className="add-employee-form-group">
              <label>Assigned College <span className="add-employee-req">*</span></label>
              {isAdmin ? (
                <select name="college" value={form.college || ''} onChange={handleInputChange}>
                  <option value="">-- Select College --</option>
                  {(user?.colleges || []).map(c => (
                    <option key={c._id} value={c._id}>{c.name}</option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  value={user?.collegeName || ''}
                  disabled
                  style={{ background: '#f1f5f9', cursor: 'not-allowed' }}
                />
              )}
            </div>
          </div>

          {/* Section: Professional Info */}
          <h4 className="add-employee-section-title">Professional Details</h4>
          <div className="add-employee-form-grid">
            <div className="add-employee-form-group">
              <label>Qualification</label>
              <input type="text" name="qualification" value={form.qualification} onChange={handleInputChange} placeholder="e.g. M.Sc, PhD" />
            </div>
            <div className="add-employee-form-group">
              <label>Department <span className="add-employee-req">*</span></label>
              <select 
                name="department" 
                value={form.department} 
                onChange={handleInputChange}
                disabled={isAdmin && !form.college}
              >
                <option value="">{isAdmin && !form.college ? '-- Select College First --' : '-- Select Department --'}</option>
                {departments.map(d => <option key={d._id} value={d._id}>{d.name}</option>)}
              </select>
            </div>
            <div className="add-employee-form-group">
              <label>Experience</label>
              <select name="experience" value={form.experience} onChange={handleInputChange}>
                <option value="">-- Select --</option>
                {EXPERIENCES.map(ex => <option key={ex} value={ex}>{ex}</option>)}
              </select>
            </div>
            <div className="add-employee-form-group add-employee-col-span-2">
              <label>Last Employer</label>
              <input type="text" name="lastEmployer" value={form.lastEmployer} onChange={handleInputChange} placeholder="Previous company/college" />
            </div>
          </div>

          {/* Section: Financial Info */}
          <h4 className="add-employee-section-title">Financial Details</h4>
          <div className="add-employee-form-grid">
            <div className="add-employee-form-group">
              <label>Bank Name</label>
              <input type="text" name="bankName" value={form.bankName} onChange={handleInputChange} placeholder="e.g. State Bank of India" />
            </div>
            <div className="add-employee-form-group">
              <label>Account Number</label>
              <input type="text" name="accountNumber" value={form.accountNumber} onChange={handleInputChange} placeholder="e.g. 1234567890" />
            </div>
            <div className="add-employee-form-group">
              <label>IFSC Code</label>
              <input type="text" name="ifscCode" value={form.ifscCode} onChange={handleInputChange} placeholder="e.g. SBIN0001234" />
            </div>
            <div className="add-employee-form-group">
              <label>Account Type</label>
              <select name="accountType" value={form.accountType} onChange={handleInputChange}>
                <option value="">-- Select --</option>
                <option value="Savings">Savings</option>
                <option value="Current">Current</option>
                <option value="Salary Account">Salary Account</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div className="add-employee-form-group">
              <label>Monthly Salary (₹) <span className="add-employee-req">*</span></label>
              <input type="number" name="monthlySalary" value={form.monthlySalary} onChange={handleInputChange} placeholder="e.g. 25000" min="0" />
            </div>
            <div className="add-employee-form-group">
              <label>PF Deduction (%)</label>
              <input type="number" name="pfDeduction" value={form.pfDeduction} onChange={handleInputChange} placeholder="e.g. 12" min="0" max="100" />
            </div>
            <div className="add-employee-form-group">
              <label>ESI Deduction (%)</label>
              <input type="number" name="esiDeduction" value={form.esiDeduction} onChange={handleInputChange} placeholder="e.g. 0.75" min="0" max="100" />
            </div>
            <div className="add-employee-form-group">
              <label>Net Daily Salary (₹) <span style={{fontSize:'0.75rem',color:'#64748b'}}>(Monthly ÷ 30)</span></label>
              <input type="number" name="netDailySalary" value={form.netDailySalary} readOnly placeholder="Auto-calculated" min="0" style={{background:'#f1f5f9',cursor:'not-allowed'}} />
            </div>
            <div className="add-employee-form-group">
              <label>Security Money (₹)</label>
              <input type="number" name="securityMoney" value={form.securityMoney} onChange={handleInputChange} placeholder="e.g. 5000" min="0" />
            </div>
            <div className="add-employee-form-group">
              <label>Monthly Deduction (₹)</label>
              <input type="number" name="monthlyDeduction" value={form.monthlyDeduction} onChange={handleInputChange} placeholder="e.g. 1000" min="0" />
            </div>
          </div>

          <div className="add-employee-form-buttons">
            <button className="add-employee-btn-cancel" onClick={resetForm} disabled={saving}>Cancel</button>
            <button className="add-employee-btn-confirm" onClick={handleSubmit} disabled={saving}>
              {saving ? 'Saving...' : editId ? 'Update Employee' : 'Save Employee'}
            </button>
          </div>
        </div>
      )}

      {/* ── Table ── */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b' }}>Loading employees...</div>
      ) : Object.keys(grouped).length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#64748b', background: '#fff', borderRadius: '8px' }}>No employees found.</div>
      ) : (
        <div className="add-employee-grouped-container">
          {Object.entries(grouped).map(([collegeName, emps]) => (
            <div key={collegeName} className="add-employee-table-group">
              <h3 className="add-employee-group-header">🏫 {collegeName}</h3>
              <div className="add-employee-table-wrapper">
                <table className="add-employee-table">
            <thead>
              <tr>
                <th>S.No</th>
                <th className="add-employee-align-left">Employee Name</th>
                <th className="add-emp-table-fname">Father's Name</th>
                <th className="add-emp-table-contact">Contact</th>
                <th className="add-emp-table-mail">Mail</th>
                <th className="add-emp-table-exp">Experience</th>
                <th className="add-emp-table-status">Status</th>
                <th className="add-emp-table-sallog">Salary Log</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {emps.length === 0 ? (
                <tr><td colSpan="9" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No employees found.</td></tr>
              ) : emps.map((emp, i) => (
                <React.Fragment key={emp._id}>
                  <tr className="add-employee-main-row">
                    <td>{i + 1}</td>
                    <td className="add-employee-align-left">
                      <div className="add-employee-name-wrapper" onClick={() => setExpandedRow(expandedRow === emp._id ? null : emp._id)}>
                        <span style={{ fontWeight: 600 }}>{emp.name}</span>
                        <button className="add-employee-expand-btn">
                          {expandedRow === emp._id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </div>
                    </td>
                    <td className="add-emp-table-fname">{emp.fatherName || '—'}</td>
                    <td className="add-emp-table-contact">{emp.contact || '—'}</td>
                    <td className="add-emp-table-mail">{emp.email || '—'}</td>
                    <td className="add-emp-table-exp"><span className="add-employee-badge">{emp.experience || '—'}</span></td>
                    <td className="add-emp-table-status">
                      <label className="add-employee-switch">
                        <input type="checkbox" checked={emp.isActive} onChange={() => toggleStatus(emp)} />
                        <span className="add-employee-slider" />
                      </label>
                      <div style={{ fontSize: '0.7rem', marginTop: '4px', color: emp.isActive ? '#27ae60' : '#e74c3c', fontWeight: '600' }}>
                        {emp.isActive ? 'Active' : 'Disabled'}
                      </div>
                    </td>
                    <td className="add-emp-table-sallog">
                      <button
                        className="add-employee-btn-sallog"
                        title="Salary Log"
                        onClick={() => navigate(`${basePath}/salary/log/${emp._id}`)}
                      >
                        <FaHistory /> Log
                      </button>
                    </td>
                    <td>
                      <div className="add-employee-action-btns">
                        <button className="add-employee-btn-edit" onClick={() => handleEdit(emp)} title="Edit"><FaEdit /></button>
                        <button className="add-employee-btn-delete" onClick={() => handleDelete(emp)} title="Delete"><FaTrash /></button>
                      </div>
                    </td>
                  </tr>

                  {/* Expanded Row */}
                  {expandedRow === emp._id && (
                    <tr className="add-employee-expanded-row">
                      <td colSpan="9">
                        <div className="add-employee-expanded-content" style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '20px', padding: '20px' }}>

                          {/* Photo spans 2 rows */}
                          <div style={{ gridRow: '1 / 3' }}>
                            {getPhotoUrl(emp) ? (
                              <img src={getPhotoUrl(emp)} alt={emp.name} className="add-employee-expanded-img" style={{ width: '120px', height: '155px', borderRadius: '8px', objectFit: 'cover', border: '1px solid #cbd5e1', boxShadow: '0 4px 8px rgba(0,0,0,0.08)', display: 'block' }} onError={(e) => e.target.style.display='none'}/>
                            ) : (
                              <div style={{ width: '120px', height: '155px', borderRadius: '8px', background: '#f1f5f9', border: '1px solid #cbd5e1', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <FaCamera style={{ fontSize: '2rem', color: '#94a3b8' }} />
                              </div>
                            )}
                          </div>

                          <div className="add-employee-expanded-grid">
                            {/* Dynamically shown based on table hiding */}
                            <div className="add-employee-exp-item add-emp-exp-status">
                              <span className="add-employee-exp-label">Status</span>
                              <span className="add-employee-exp-value" style={{ color: emp.isActive ? '#27ae60' : '#e74c3c' }}>{emp.isActive ? 'Active' : 'Disabled'}</span>
                            </div>
                            <div className="add-employee-exp-item add-emp-exp-fname">
                              <span className="add-employee-exp-label">Father's Name</span>
                              <span className="add-employee-exp-value">{emp.fatherName || '—'}</span>
                            </div>
                            <div className="add-employee-exp-item add-emp-exp-contact">
                              <span className="add-employee-exp-label">Contact</span>
                              <span className="add-employee-exp-value">{emp.contact || '—'}</span>
                            </div>
                            <div className="add-employee-exp-item add-emp-exp-mail">
                              <span className="add-employee-exp-label">Mail</span>
                              <span className="add-employee-exp-value">{emp.email || '—'}</span>
                            </div>
                            <div className="add-employee-exp-item add-emp-exp-exp">
                              <span className="add-employee-exp-label">Experience</span>
                              <span className="add-employee-exp-value">{emp.experience || '—'}</span>
                            </div>

                            {/* Always visible in expand */}
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">DOB</span><span className="add-employee-exp-value">{emp.dob ? new Date(emp.dob).toLocaleDateString() : '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Gender</span><span className="add-employee-exp-value">{emp.gender || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Religion</span><span className="add-employee-exp-value">{emp.religion || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Category</span><span className="add-employee-exp-value">{emp.category || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Qualification</span><span className="add-employee-exp-value">{emp.qualification || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Department</span><span className="add-employee-exp-value">{emp.department?.name || emp.department || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Role Type</span><span className="add-employee-exp-value">{emp.teachingType || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Last Employer</span><span className="add-employee-exp-value">{emp.lastEmployer || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Monthly Salary</span><span className="add-employee-exp-value">{emp.monthlySalary ? `₹ ${emp.monthlySalary}` : '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">PF Deduction</span><span className="add-employee-exp-value">{emp.pfDeduction ? `${emp.pfDeduction}%` : '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">ESI Deduction</span><span className="add-employee-exp-value">{emp.esiDeduction ? `${emp.esiDeduction}%` : '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Net Daily Salary</span><span className="add-employee-exp-value">{emp.netDailySalary ? `₹ ${emp.netDailySalary}` : '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Address</span><span className="add-employee-exp-value">{emp.address || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">City</span><span className="add-employee-exp-value">{emp.city || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">State</span><span className="add-employee-exp-value">{emp.state || '—'}</span></div>
                            <div className="add-employee-exp-item"><span className="add-employee-exp-label">Country</span><span className="add-employee-exp-value">{emp.country || '—'}</span></div>
                          </div>

                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
              </table>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AddEmployee;